import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { FileInfo } from '@classes/file/file-info';
import { FileProvider } from '@classes/file/file-provider';
import { FileUpload } from '@classes/file/file-upload';
import { FileImport } from '@classes/file/file-import';
import { UiService } from './ui.service';
import { FileSource } from '@enums/file-source.enum';
import { FilesLists } from '@classes/file/files-lists';
import { FileConfig } from '@classes/file/file-config';
import { FileDetail } from '@classes/file/file-detail';

@Injectable({
    providedIn: 'root'
})
export class FileService {
    processingStateChanged = new Subject<boolean>();
    filesListsChanged = new Subject<FilesLists>();

    private baseUrl = environment.apiUrl + 'files';
    private filesLists: FilesLists;

    constructor(
        private http: HttpClient,
        private uiSrv: UiService
    ) { }

    isEnabledJobImport(): Observable<boolean> {
        return this.http.get<boolean>(`${environment.apiUrl}job/fininst`)
    }
    setIsEnabledJobImport(isEnabled: boolean): Observable<void> {
        const url = `${environment.apiUrl}job/fininst-${isEnabled ? 'enable' : 'disable'}`;
        return this.http.post<void>(url, null);
    }

    uploadSimple(file: Blob, filename = 'file'): Observable<FileInfo> {
        const formData = new FormData();
        formData.append('file', file, filename);

        return this.http.post<FileInfo>(this.baseUrl, formData);
    }

    uploadWithProgress(file: FileUpload): Observable<FileUpload> {
        const formData = new FormData();
        const fileName = file.file.name;
        formData.append('file', file.file, fileName);

        return this.http.post<FileInfo>(this.baseUrl, formData, {
            reportProgress: true,
            observe: 'events'
        }).pipe(
            map(event => this.processUploadEvent(event, file))
        );
    }

    import(fileImport: FileImport): Observable<void> {
        this.processingStateChanged.next(true);

        return this.http.post<void>(`${this.baseUrl}/import`, fileImport)
        .pipe(
            tap(() => {
                this.processingStateChanged.next(false);
                this.uiSrv.showSnackbar('files.processingStates.success.fullLabel', true);
            })
        );
    }
    importSystem(filesInfo: FileInfo[], destFilePath: string, folderCode: string): Observable<void> {
        const headers = new HttpHeaders()
            .set('providerCode', destFilePath)
            .set('folderCode', folderCode);
        return this.http.post<void>(`${this.baseUrl}/import-system`, filesInfo, { headers })
    }
    getFolders(providerCode: string): Observable<string[]> {
        const params = new HttpParams()
            .set('providerCode', providerCode);
        return this.http.get<string[]>(`${this.baseUrl}/folders`, { params });
    }
    getFiles(): Observable<FilesLists> {
        this.uiSrv.loadingStateChanged.next(true);

        return this.http.get<FilesLists>(this.baseUrl)
        .pipe(
            tap(filesLists => {
                this.filesLists = filesLists;
                this.filesListsChanged.next(filesLists);
                this.uiSrv.loadingStateChanged.next(false);
            })
        );
    }

    getSystems (): Observable<FileDetail[]> {
        return this.http.get<FileDetail[]>(`${this.baseUrl}/system`);
    }

    delete(id: number, source: FileSource): Observable<void> {
        this.uiSrv.loadingStateChanged.next(true);

        const httpParams = new HttpParams()
            .append("source", source.toString());

        return this.http.delete<void>(`${this.baseUrl}/${id}`, {params: httpParams})
        .pipe(
            tap(() => {
                switch(source) {
                    case FileSource.simulation:
                        this.filesLists.simulationFiles = this.filesLists.simulationFiles
                            .filter(f => f.id !== id);
                        break;
                    case FileSource.provider:
                        this.filesLists.sourceFiles = this.filesLists.sourceFiles
                            .filter(f => f.id !== id);
                        break;
                }
                this.filesListsChanged.next(this.filesLists);
                this.uiSrv.loadingStateChanged.next(false);
            })
        );
    }

    // TODO: merge 2 next methods
    getProviderFiles(): Observable<FileConfig[]> {
        const httpParams = new HttpParams()
            .append("source", FileSource.provider.toString());

        return this.http.get<FileConfig[]>(this.baseUrl, {params: httpParams});
    }

    getSimulationFiles(): Observable<FileConfig> {
        const httpParams = new HttpParams()
            .append("source", FileSource.simulation.toString());

        return this.http.get<FileConfig>(this.baseUrl, {params: httpParams});
    }

    getFilesProviders(): Observable<FileProvider[]> {
        return this.http.get<FileProvider[]>(`${this.baseUrl}/providers`);
    }

    private processUploadEvent(event: HttpEvent<FileInfo>, file: FileUpload): FileUpload {
        switch(event.type) {
            case HttpEventType.UploadProgress:
                file.progress = Math.round(100 * event.loaded / event.total);
                break;
            case HttpEventType.Response:
                file.progress = 100;
                file.fileInfo = event.body;
                break;
        }

        return file;
    }
}
